import { useFpB2bCartService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-service'
import { useFpB2bCoupons } from '@cart-services/hooks/fp-b2b/use-fp-b2b-coupons'
import { type AllProductDTO } from '@cart-services/types'
import { CollapseProductButton } from '@cart-ui/CollapseProduct/CollapseProductButton'
import { CollapseProductChip } from '@cart-ui/CollapseProductChip/CollapseProductChip'
import { CollapseProductContent } from '@cart-ui/CollapseProductContent/CollapseProductContent'
import { CouponCondition } from '@cart-ui/CouponCondition/CouponCondition'
import { type CountryCode, type ProjectCode } from '@constants'
import { useFastpassMerchantGoogleAnalyticEvent } from '@dni-analysis'
import { AppliedStatus } from '@hooks-api/types/dataModel/coupon'
import { Collapse } from '@pets-ui/Collapse/Collapse'
import { HStack } from '@pets-ui/HStack/HStack'
import { Paper } from '@pets-ui/Paper/Paper'
import { VStack } from '@pets-ui/VStack/VStack'
import { sumOfArray } from '@utils/sumOfArray'
import { useEffect, useMemo, useState } from 'react'
import { useProductCatalog } from '../../../../fp-b2b-shared/hooks/use-product-catalog'
import { MerchantCollapseProductItem } from './MerchantCollapseProductItem'

export interface CollapseProductProps {
  parentProductGroup: AllProductDTO
  projectCode: ProjectCode
  countryCode: CountryCode
}

export const CollapseProduct = ({
  parentProductGroup,
  projectCode,
  countryCode,
}: CollapseProductProps) => {
  const { campaignId, currency, campaignCode, platform } = useProductCatalog()
  const { getMergedCouponCriterions } = useFpB2bCoupons(campaignId, projectCode, countryCode, campaignCode, platform)
  const { items: cartItems } = useFpB2bCartService(campaignId, platform)
  const { sendClickProductPictureEvent, sendClickProductNameEvent } = useFastpassMerchantGoogleAnalyticEvent({ projectCode, countryCode, campaignId })
  const [open, setOpen] = useState(true)
  const firstLevelButtonKeys = useMemo(() => Object.keys(parentProductGroup.variationsGroupByFirstLevelButton), [parentProductGroup.variationsGroupByFirstLevelButton])
  const [currentGroup, setCurrentGroup] = useState<string>()
  const currentVariations = useMemo(() => currentGroup ? parentProductGroup.variationsGroupByFirstLevelButton[currentGroup] : [], [currentGroup, parentProductGroup.variationsGroupByFirstLevelButton])

  const countProduct = useMemo(() => sumOfArray(cartItems, (acc, { product_id, quantity = 0 }) => {
    if (product_id === parentProductGroup.id) {
      return acc + quantity
    }
    return acc
  }), [cartItems, parentProductGroup.id])

  const handleCollapse = () => {
    setOpen(!open)
  }

  const couponIds = useMemo(() => [...new Set(currentVariations?.flatMap(variation => variation.coupon_ids || []) || [])], [currentVariations])
  const couponConditions = useMemo(() => getMergedCouponCriterions(couponIds), [couponIds, getMergedCouponCriterions])
  const buttonBaseId = parentProductGroup.title ? projectCode + '-' + parentProductGroup.title : ''

  useEffect(() => {
    if (firstLevelButtonKeys.length > 0) {
      setCurrentGroup(firstLevelButtonKeys[0])
    }
    return () => {
      setCurrentGroup(undefined)
    }
  }, [firstLevelButtonKeys])

  return (
    <Paper elevation={0} px="8px">
      <CollapseProductButton
        id={buttonBaseId}
        onClick={handleCollapse}
        thumbnail={parentProductGroup.thumbnail}
        title={parentProductGroup.title}
        countProduct={countProduct}
        onProductImageClick={sendClickProductPictureEvent}
        onProductNameClick={sendClickProductNameEvent}
        showProductSalesTag={couponIds.length > 0}
      />
      <Collapse in={open}>
        <CollapseProductContent>
          {couponConditions.length > 0 && (
            <VStack gap={2} p={2}>
              {couponConditions.map(criterion => (
                <CouponCondition
                  key={criterion.display_name}
                  isApplied={criterion.applied_status === AppliedStatus.APPLIED || criterion.applied_status === AppliedStatus.UNABLE}
                  displayName={criterion.display_name}
                  currency={currency}
                  moreAmount={criterion.left_amount}
                  moreQuantity={criterion.left_quantity ?? criterion.display_criterion_quantity}
                  count={criterion.count}
                />
              ))}
            </VStack>
          )}
          {firstLevelButtonKeys.length > 1 && (
            <HStack gap="8px" padding="8px">
              {firstLevelButtonKeys.map((firstLevelButton) => {
                const groupId = projectCode + '-' + parentProductGroup.title + '-' + firstLevelButton
                const isSelected = firstLevelButton === currentGroup
                return (
                  <CollapseProductChip
                    id={groupId}
                    key={firstLevelButton}
                    onClick={() => setCurrentGroup(firstLevelButton)}
                    isSelected={isSelected}
                  >
                    {firstLevelButton}
                  </CollapseProductChip>
                )
              })}
            </HStack>
          )}
          {currentVariations?.map((variation) => {
            const currentQty = cartItems.find(item => item.cartItemId === variation.variation_id)?.quantity || 0
            const productId = projectCode + '-' + variation.sku
            return (
              <MerchantCollapseProductItem
                id={productId}
                key={variation.sku}
                variation={variation}
                currentQty={currentQty}
              />
            )
          })}
        </CollapseProductContent>
      </Collapse>
    </Paper>
  )
}
